/**
 * Cart "Get the Set" — toggle the per-line-item set panel and add the
 * selected set product to the cart without a full reload.
 *
 * Listens once at document level so it survives cart-items-component
 * morphSection re-renders without needing re-binding.
 *
 * Dispatches a 'cart:update' CustomEvent whose detail.data.sections
 * matches CartUpdateEvent's shape, which cart-items-component
 * already listens for and uses to morph itself.
 */

const TRIGGER_SELECTOR = '[data-set-trigger]';
const PANEL_SELECTOR = '[data-set-panel]';
const FORM_SELECTOR = '[data-set-form]';
const ADD_SELECTOR = '[data-set-add]';

function getCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    const id = el instanceof HTMLElement ? el.dataset.sectionId : null;
    if (id) ids.add(id);
  });
  return Array.from(ids).join(',');
}

function findPanel(line) {
  return document.querySelector(`${PANEL_SELECTOR}[data-set-panel="${CSS.escape(line)}"]`);
}

function setPanelOpen(trigger, panel, open) {
  if (open) {
    panel.removeAttribute('hidden');
    trigger.setAttribute('aria-expanded', 'true');
  } else {
    panel.setAttribute('hidden', '');
    trigger.setAttribute('aria-expanded', 'false');
  }
}

function getSetFormData(container) {
  const formData = new FormData();

  container.querySelectorAll('input[name], select[name]').forEach((field) => {
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement) || field.disabled) return;
    formData.append(field.name, field.value);
  });

  return formData;
}

async function addSetToCart(container, submit) {
  const variantSelect = container.querySelector('select[name="id"]');
  if (variantSelect instanceof HTMLSelectElement && !variantSelect.value) {
    variantSelect.focus();
    return;
  }

  submit.disabled = true;

  const cartAddUrl =
    (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';

  const formData = getSetFormData(container);
  const sectionIds = getCartSectionIds();
  if (sectionIds) formData.set('sections', sectionIds);

  try {
    const response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });
    const payload = await response.json();

    if (!response.ok || payload.status) {
      // eslint-disable-next-line no-console
      console.error('[cart-get-the-set] add failed', payload);
      return;
    }

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: payload,
          sourceId: 'cart-get-the-set',
          data: {
            source: 'cart-get-the-set',
            sections: payload.sections,
            variantId: String(payload.variant_id || ''),
            itemCount: payload.items_count,
          },
        },
      })
    );

    const panel = container.closest(PANEL_SELECTOR);
    const panelLine = panel?.getAttribute('data-set-panel');
    if (panel && panelLine) {
      const trigger = document.querySelector(
        `${TRIGGER_SELECTOR}[data-set-line="${CSS.escape(panelLine)}"]`
      );
      if (trigger) setPanelOpen(trigger, panel, false);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[cart-get-the-set] network error', error);
  } finally {
    submit.disabled = false;
  }
}

if (!window.__paragonCartGetSetListenersBound) {
  window.__paragonCartGetSetListenersBound = true;

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;
    const line = trigger.getAttribute('data-set-line');
    if (!line) return;
    const panel = findPanel(line);
    if (!panel) return;
    const isOpen = !panel.hasAttribute('hidden');
    setPanelOpen(trigger, panel, !isOpen);
  });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const submit = target.closest(ADD_SELECTOR);
    if (!(submit instanceof HTMLButtonElement)) return;
    const container = submit.closest(FORM_SELECTOR);
    if (!(container instanceof HTMLElement)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    addSetToCart(container, submit);
  }, true);
}
